import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError, useAuth } from '@saas-kit/lib';
import { toast } from '@saas-kit/ui';

/**
 * Shared list + create/update/delete plumbing for the QMS setup tabs.
 * Each tab supplies its own API functions and renders its own form.
 */
export function useQmsCrud<T, C>(opts: {
  key: string;
  list: () => Promise<T[]>;
  create: (input: C) => Promise<T>;
  update: (id: string, input: Record<string, unknown>) => Promise<T>;
  remove: (id: string) => Promise<void>;
  enabled: boolean;
}) {
  const { t } = useTranslation('qms');
  const tenantId = useAuth((s) => s.selectedTenant?.tenant_id);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [opts.key, tenantId],
    queryFn: opts.list,
    enabled: opts.enabled,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [opts.key] });
  const fail = (e: unknown) => toast.error(parseApiError(e).message);

  const createMutation = useMutation({
    mutationFn: opts.create,
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.created'));
    },
    onError: fail,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      opts.update(id, input),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.updated'));
    },
    onError: fail,
  });

  const deleteMutation = useMutation({
    mutationFn: opts.remove,
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.deleted'));
    },
    onError: fail,
  });

  const deleteManyMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await opts.remove(id);
    },
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.deleted'));
    },
    onError: fail,
  });

  return {
    items: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    create: createMutation.mutateAsync,
    creating: createMutation.isPending,
    update: (id: string, input: Record<string, unknown>) =>
      updateMutation.mutate({ id, input }),
    remove: deleteMutation.mutate,
    removeMany: deleteManyMutation.mutateAsync,
    removing: deleteMutation.isPending,
  };
}
